import { db } from './firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { validateAndDetectChain } from './chainUtils';
import { normalizeAddress, isEVMAddress } from './addressUtils';

/**
 * Universal search supporting addresses and coins
 * @param {string} searchTerm - User input (address, coin name, or symbol)
 * @param {number} maxResults - Max results per category (default: 5)
 * @returns {Promise<SearchResults>}
 */
export async function universalSearch(searchTerm, maxResults = 5) {
  const term = searchTerm.trim();

  if (!term || term.length < 2) {
    return { addresses: [], coins: [], totalResults: 0 };
  }

  // Run searches in parallel
  const [addressResults, coinResults] = await Promise.all([
    searchAddresses(term, maxResults),
    searchCoins(term, maxResults)
  ]);

  return {
    addresses: addressResults,
    coins: coinResults,
    totalResults: addressResults.length + coinResults.length
  };
}

/**
 * Search for addresses
 * Returns valid addresses with chain detection
 * For EVM addresses (0x...), returns as a generic address that will be expanded by UI
 */
async function searchAddresses(term, maxResults) {
  try {
    // Normalize the term first to handle whitespace, case, etc.
    const normalized = normalizeAddress(term);

    // Check if it's an EVM address (hex format) after normalization
    if (isEVMAddress(normalized)) {
      // Return as generic EVM address - UI will expand to all chains
      return [{
        type: 'address',
        address: normalized,
        chain: 'ethereum', // Default chain, will be overridden by UI expansion
        displayText: normalized, // Use normalized format for consistency
        searchScore: 100 // Exact match gets highest score
      }];
    }

    // For non-EVM addresses (Bitcoin, Litecoin, etc.)
    const validation = validateAndDetectChain(term);

    if (!validation.valid) {
      return [];
    }

    const normalizedWithChain = normalizeAddress(term, validation.chain);

    // Return the validated address with its specific chain
    return [{
      type: 'address',
      address: normalizedWithChain,
      chain: validation.chain,
      displayText: term, // Keep original format for display
      searchScore: 100 // Exact match gets highest score
    }];
  } catch (error) {
    console.error('Error searching addresses:', error);
    return [];
  }
}

/**
 * Search for coins by name, symbol, or contract address
 * Uses multi-strategy approach for best results
 */
async function searchCoins(term, maxResults) {
  const termLower = term.toLowerCase();
  const results = new Map(); // Use Map to deduplicate by coin ID

  try {
    // Strategy 1: Try exact symbol/word match first (most common)
    // This catches: "btc" -> Bitcoin, "uni" -> Uniswap
    try {
      // Note: We fetch more results and sort in JS because Firestore's orderBy
      // excludes documents where the orderBy field is null
      const symbolQuery = query(
        collection(db, 'coins'),
        where('searchTokens', 'array-contains', termLower),
        limit(maxResults * 5) // Fetch more to account for filtering
      );

      const symbolSnapshot = await getDocs(symbolQuery);
      symbolSnapshot.forEach(doc => {
        const data = doc.data();
        results.set(doc.id, {
          type: 'coin',
          id: doc.id,
          ...data,
          searchScore: calculateCoinScore(data, termLower, 'symbol')
        });
      });
    } catch (error) {
      console.error('Symbol search failed:', error);
    }

    // Strategy 2: If we don't have enough results, try prefix search on name
    // This catches: "bitco" -> Bitcoin, "uni" -> Uniswap, Universe, etc.
    if (results.size < maxResults) {
      try {
        const nameQuery = query(
          collection(db, 'coins'),
          where('nameLower', '>=', termLower),
          where('nameLower', '<', termLower + '\uf8ff'),
          orderBy('nameLower', 'asc'),
          limit(maxResults - results.size)
        );

        const nameSnapshot = await getDocs(nameQuery);
        nameSnapshot.forEach(doc => {
          if (!results.has(doc.id)) {
            const data = doc.data();
            results.set(doc.id, {
              type: 'coin',
              id: doc.id,
              ...data,
              searchScore: calculateCoinScore(data, termLower, 'name')
            });
          }
        });
      } catch (error) {
        console.error('Name prefix search failed:', error);
      }
    }

  } catch (error) {
    console.error('Error searching coins:', error);
  }

  // Convert Map to array and sort by search score + market cap rank
  return Array.from(results.values())
    .sort((a, b) => {
      // First by search score (higher is better)
      if (a.searchScore !== b.searchScore) {
        return b.searchScore - a.searchScore;
      }
      // Then by market cap rank (lower is better)
      return (a.market_cap_rank || 9999) - (b.market_cap_rank || 9999);
    })
    .slice(0, maxResults);
}

/**
 * Calculate search relevance score for a coin
 * Higher score = more relevant
 */
function calculateCoinScore(coin, searchTerm, matchType) {
  let score = 0;

  // Base score by match type
  if (matchType === 'symbol') {
    // Exact symbol match is highly relevant
    if (coin.symbol?.toLowerCase() === searchTerm) {
      score += 100;
    } else {
      score += 50; // Word match in searchTokens
    }
  } else if (matchType === 'name') {
    const nameLower = coin.nameLower || '';
    if (nameLower === searchTerm) {
      score += 90; // Exact name match
    } else if (nameLower.startsWith(searchTerm)) {
      score += 70; // Prefix match
    } else {
      score += 30; // Contains match
    }
  }

  // Boost score for popular coins (top 100)
  if (coin.market_cap_rank && coin.market_cap_rank <= 100) {
    score += Math.max(0, 100 - coin.market_cap_rank);
  }

  return score;
}

/**
 * Get top coins by market cap (for autocomplete suggestions)
 */
export async function getTopCoins(limitCount = 20) {
  try {
    const topCoinsQuery = query(
      collection(db, 'coins'),
      orderBy('market_cap_rank', 'asc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(topCoinsQuery);
    return snapshot.docs.map(doc => ({
      type: 'coin',
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching top coins:', error);
    return [];
  }
}

/**
 * Get a single coin by ID
 */
export async function getCoinById(coinId) {
  try {
    const coinDoc = await db.collection('coins').doc(coinId).get();
    if (coinDoc.exists) {
      return {
        type: 'coin',
        id: coinDoc.id,
        ...coinDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching coin:', error);
    return null;
  }
}
